// 千日手の軍配者たち (hollow-actor の作り直し) をゲーム本体の一戦にする (2026-10-29・開発版の `?fight=hollow-actor` だけ)。
//   技と挟み撃ちの決まりは src/twins_proxy_plan.ts、動きの元はラボ src/twins_proxy_lab.ts (twins-proxy-lab.html)。
//   ゲームはボスを1体 (state.boss) しか持たないので、二人と装置はこの部品が持ち、state.boss は「いま斬れる所」を指す影にする:
//     - boss.x = 主人公に一番近い立っている方 (3部屋目は斬れる部品の位置。無い時は遠くへ置いて当たらないようにする)。
//     - boss.hp = 床 + 二人 (3部屋目は装置) の残り。二人はそれぞれ部屋の幅の半分を持ち、使い切ると膝をついて攻撃をやめる。
//       二人とも倒れると hp が床に届き、普段の崩れ → 致命になる (二人まとめて1回)。致命の後の部屋で二人とも立ち直る。
//     - boss.posture = 二人で1本の体幹。満ちると崩れる (2.6秒・この間は削りが3倍)。崩れても致命にはならない (hp が残る間)。
//   二人の当たりは本物の当たり (ActiveHit) にして updateHits へ渡す＝パリィ・回避・被弾は他のボスと同じ仕組みを通る。
//   崩れて致命を待つ間・致命の間・撃破の後は、今の updateBoss をそのまま通す (崩れからの戻り・致命・とどめを使い回す)。
//   部屋: 1 = 二人 / 2 = 二人を速く (作者 t5「同じ技でテンポを上げる」。不安定にする案は答えるページで出す) / 3 = 奥の装置。
//   FX と音は仮 (作者 c1)。絵は二人・装置とも描いた絵が来るまで、図形の代わり (src/twins_fight_draw.ts)。
import type { ActiveHit, Boss, Player, ScreenShake, SfxId } from './types'
import { ARENA, arenaBoundsFor } from './balance'
import { BOSS_DEFINITIONS } from './bosses'
import { chipFloor, roomSize } from './combat'
import { TWINS_REVEAL_SFX_PEAK_MS, twinsSfxDevice, twinsSfxHit, twinsSfxReveal, twinsSfxTell } from './boss_sfx_v1'
import { BULLET_SPEED, SAME_BEAT_MS, absHits, earliestStart, pickable, redGlowFrom, type BookedHit, type TwinId, type TwinMove } from './twins_proxy_plan'

export const TWINS_BOSS_ID = 'hollow-actor'
export const TWINS_NAME = '千日手の軍配者たち'
export const TWINS_ROOM_COUNT = 3
let enabled = false
/** 開発版の一戦にする (main.ts が `?fight=hollow-actor` の時に呼び、その後でボスを作り直す)。
 *  名前と部屋の数だけ書き換える。技の一覧 (unlockByExecutes) は使わない (二人の技はこの部品が出す)。 */
let original: { name: string; title: typeof BOSS_DEFINITIONS[number]['title']; roomCount: typeof BOSS_DEFINITIONS[number]['roomCount'] } | null = null
export function useTwinsFight(on = true): void {
  enabled = on; fight = null
  const def = BOSS_DEFINITIONS.find((b) => b.id === TWINS_BOSS_ID)!
  if (!on) {
    if (original) { def.name = original.name; def.title = original.title; def.roomCount = original.roomCount; original = null }
    return
  }
  original ??= { name: def.name, title: def.title, roomCount: def.roomCount }
  def.name = TWINS_NAME
  def.title = 'The Sennichite Tacticians'
  def.roomCount = TWINS_ROOM_COUNT
}
export function twinsFightOn(boss: Boss | null | undefined): boolean {
  return enabled && !!boss && boss.id === TWINS_BOSS_ID
}

/** 二人の部屋の背景 (灰の城の一室・10-30 作者 c1「この城の一角、ひと部屋」)。開発版の二人の戦いの時だけ。 */
export const TWINS_BG_SRC = '/bg/twins-room.png'
export function twinsBgSrc(bossId: string | undefined): string | undefined {
  return enabled && bossId === TWINS_BOSS_ID ? TWINS_BG_SRC : undefined
}

/** 崩れの長さと、崩れの間の削りの倍率 (ラボと同じ)。 */
export const TWINS_BREAK_MS = 2600
export const TWINS_BREAK_DAMAGE_MUL = 3
/** 主人公の一太刀で減る割合 (ラボの hp60 に 7 = 1人ぶんの約 1/9)。本物の攻撃力は使わず、この割合で揃える。 */
export const TWIN_HIT_SHARE = 7 / 60
/** 跳び越えの速さ・短くても見せる長さ・高さ (px)。立ち位置で二人・倒れた体と空ける間。 */
export const HOP_SPEED = 620
export const HOP_MIN_MS = 520
export const HOP_HEIGHT = 170
export const TWIN_GAP = 120
/** 跳び越えの今の高さ (描画用・床から上へ)。 */
export function hopLift(t: TwinState, now: number): number {
  if (!t.hop) return 0
  const u = Math.min(1, Math.max(0, (now - t.hop.start) / t.hop.dur))
  return 4 * HOP_HEIGHT * u * (1 - u)
}
/** 2部屋目の速さ (技の時間をこの倍に縮める)。 */
export const ROOM2_TEMPO = 0.82

const DMG = { parry: 12, dodge: 22, bullet: 8, device: 20 } as const

// ---- 案A (11-03 作者の答え・開発版 ?twinsPlanA=1 だけ。配布版の既定は変えない) ----
//   作者の答え: 「A 倒れは一時・二人倒れたら『総崩れ』・致命は体幹だけ」「A 総崩れ：二人とも数秒動けず、斬るほど体幹が貯まる」。
//   - 片方を削り切ると倒れるが、数秒で起き上がる (体力を少し戻して)。
//   - 二人とも倒れたら総崩れ: 二人とも動けず、斬るたびに体幹が大きく貯まり、戻りも止まる。終わると二人とも起き上がる。
//   - 致命は体幹が満ちた時だけ (体力が床に届いても致命にならない)。体幹で崩れたら、2.6秒の崩れではなくそのまま致命へ。
//   - 弾き返した鈴は体幹だけを削る (体力は削らない)。
//   3部屋目の装置も体幹で取る (11-04 作者の答え room3=posture)。装置の体力は床の1つ上で止まり、体幹が満ちたらそのまま致命。
//   11-04 作者の答え (answers/twins-plan-a-1104): 倒れは4秒 (length=4000)・震えてから立つ (rise=c)・総崩れは足元に金の光 (collapse=a)。
//   秒数と割合は仮 (数値は技が揃ってから決める)。
let planA = false
export function setTwinsPlanA(on: boolean): void { planA = on; fight = null }
export function twinsPlanA(): boolean { return planA }
export const PLAN_A = {
  /** 片方が倒れている長さ (ms)。 */
  downMs: 4000,
  /** 起き上がる前に震える長さ (ms・倒れの終わりの内側)。震えが「もうすぐ起きる」の合図 (rise=c)。 */
  shakeMs: 600,
  /** 床から立ち絵へ戻るまでの長さ (ms)。この間は動かず、斬れない。 */
  riseMs: 600,
  /** 起き上がる時に戻す体力 (その一人の持ち分に対する割合)。 */
  riseBack: 0.4,
  /** 総崩れの長さ (ms)。 */
  collapseMs: 4000,
  /** 総崩れの間、一太刀で足す体幹 (最大に対する割合)。 */
  collapsePosture: 0.15,
}
/** 案A の3部屋目の部位破壊 (11-04 作者の答え part=c「壊す部品を選ぶ」・parts=ai)。壊すとその部品の技が止まり、
 *  体幹の貯まり方が部品ごとに変わる (貯まりやすくなる部品と、貯まりにくくなる部品がある)。倍率は壊した部品の掛け算。
 *  選んだ部品と理由＝docs/research/2026-11-04-twins-part-break.md。回数と倍率は仮 (遊んで決める)。 */
export type PartKind = 'wall' | 'curtain' | 'light'
export const PLAN_A_PARTS: Record<PartKind, { hits: number; mul: number }> = {
  /** 守りの駒 (落ちてくる金)。囲いが剥がれて崩れやすくなる。ただ斬れる所が1つ減る。 */
  wall: { hits: 3, mul: 1.5 },
  /** 緞帳 (左右の幕)。床まで下りた裾を斬る。隠れる幕が無くなり崩れやすくなる。 */
  curtain: { hits: 3, mul: 1.3 },
  /** 読みの照明 (装置の目)。灯りが落とす歩の駒を斬る。追ってくる灯りは止まるが、読みを失った二人は守りに入り崩れにくくなる。 */
  light: { hits: 3, mul: 0.7 },
}
/** 案A の3部屋目の形 (11-04 作者の答え room3=return「打ち返す」)。'cut' = 前の部位破壊 (`&twinsRoom3=cut` で残す)。 */
export type TwinsRoom3 = 'return' | 'cut'
let room3: TwinsRoom3 = 'return'
export function setTwinsRoom3(v: string | null): void { room3 = v === 'cut' ? 'cut' : 'return'; fight = null }
export function twinsRoom3(): TwinsRoom3 { return room3 }
const returnMode = () => planA && room3 === 'return'
/** 打ち返す版 (11-04 作者 room3=return・雷返しの型)。装置が床を叩いた後に白い駒が主人公へ飛んでくる。弾くと装置へ戻り、
 *  まだ壊れていない部品 (守りの駒 → 緞帳 → 照明の順) に当たる。2回当てた部品は壊れてその技が止まる。当てるたびに体幹が大きく増え、
 *  装置がよろめいて、駒を弾いた所に斬れる所が出る (よろめきの間は斬りの体幹が倍・体幹が戻らない)。数値は仮 (遊んで決める)。 */
export const RETURN = {
  /** 床を叩いてから白い駒が出るまで (ms)。 */
  launchMs: 400,
  /** 白い駒が出る所 (主人公から・px) と速さ (px/秒)。 */
  launchGap: 380, speed: 420,
  /** 弾いた駒が装置へ戻るまで (ms)。 */
  flyMs: 450,
  /** 部品が壊れるまでに当てる数。 */
  partHits: 2,
  /** 当てた時に足す体幹 (最大に対する割合)。 */
  posture: 0.12,
  /** よろめきの長さ (ms) と、その間の斬りの体幹の倍率。 */
  falterMs: 2000, falterMul: 2,
} as const
/** 当てる部品の順。 */
export const RETURN_PART_ORDER: PartKind[] = ['wall', 'curtain', 'light']
/** 安全地帯の技「詰めろ」(11-04 作者 safe=both・outside=hit・parry=parry)。床を8マスに分け、主人公から2〜3マス離れた1マスだけ残して
 *  ほかへ駒を一斉に落とす。落ちた駒は暫く床に残るので、安全なマスを出ると当たる。安全なマスへは白い駒が左右から突いてくる (弾ける)。
 *  形の正本＝docs/research/2026-11-04-twins-return-and-tsume.md (先代の姫が借りる時もここを写す)。数値は仮。 */
export const TSUME = {
  cells: 8,
  /** 主人公のマスから安全なマスまでの距離 (マス)。 */
  minAway: 2, maxAway: 3,
  /** 予告 (赤いマスと金のマス) の長さ (ms)。 */
  tellMs: 1800,
  /** 外へ落ちた駒が床に残る長さ (ms・この間に外へ出ると当たる)。 */
  holdMs: 2000,
  /** 安全なマスへの突き (技の始まりから ms) と、突きの出る所 (マスの端から外へ px)・速さ。2つ目は1つ目が装置へ着く前に出る (よろめきで消えない)。 */
  thrustAt: [2300, 2600], thrustGap: 300, thrustSpeed: 520,
  /** 技の長さ (ms)。 */
  endMs: 4200,
} as const
export function tsumeCells(w: { left: number; right: number }): { x0: number; cell: number } {
  return { x0: w.left, cell: (w.right - w.left) / TSUME.cells }
}
/** 主人公の位置から安全なマスを選ぶ (壁の内側で、2〜3マス離れた所)。 */
export function tsumeSafeCell(px: number, w: { left: number; right: number }, r: () => number): number {
  const { x0, cell } = tsumeCells(w)
  const pc = Math.max(0, Math.min(TSUME.cells - 1, Math.floor((px - x0) / cell)))
  const ok: number[] = []
  for (let c = 0; c < TSUME.cells; c++) { const d = Math.abs(c - pc); if (d >= TSUME.minAway && d <= TSUME.maxAway) ok.push(c) }
  return ok[Math.floor(r() * ok.length) % ok.length] ?? 0
}
export function tsumeSpan(d: DevAtk, w: { left: number; right: number }): [number, number] {
  const { x0, cell } = tsumeCells(w)
  return [x0 + cell * d.safe, x0 + cell * (d.safe + 1)]
}
/** 3幕目の吊られた二人 (11-04 作者 puppet=hang・メモ「詰めろの突き攻撃だけ２人が出すってのがよくわからないけど、普通に技のうちの１つとして、
 *  舞台装置同様複数パターンの攻撃を持ってる感じにしたい／１－２幕のオマージュ的な」)。開発版 `?twinsPlanA=1` の打ち返す版の上に載る。
 *  11-05 作者の答え plan=b で、開発版の既定は b (`src/main.ts`)。a と c は比べる用に `&twinsPuppet=a|c` で残す (作者が「混ぜる」へ動いた時に要る)。
 *  二人は常夜燈の青い糸で照明の梁から吊られ、技の時だけ床へ下ろされる。二人に当たり判定は付けない (斬れるのは装置のよろめきだけ)。
 *  弾ける当たりは白い駒 (弾くと装置へ打ち返る)。どの技をどうなぞるかは3案を比べる (決めるのは作者):
 *    a = 型をなぞる (千日手・三連打・号令をほぼそのまま)・b = 糸で崩れた型 (二段払い→振り子・三連打→糸の跳ね・滑り込み→引きずり)
 *    c = 装置と二人で一手 (装置の床の技の戻りを二人の小技が埋める＝1・2幕の決まり3「大技の戻りは相方が小技で埋める」)。
 *  詰めろの両側の突きは、どの案でも下ろされた二人が突く。数値は仮。記録＝docs/research/2026-11-05-twins-act3-puppets.md。 */
export type TwinsPuppetPlan = 'a' | 'b' | 'c'
let puppetPlan: TwinsPuppetPlan | null = null
export function setTwinsPuppet(v: string | null): void { puppetPlan = v === 'a' || v === 'b' || v === 'c' ? v : null; fight = null }
export function twinsPuppetPlan(): TwinsPuppetPlan | null { return puppetPlan }
const puppetMode = () => returnMode() && puppetPlan !== null
export const PUPPET = {
  /** 吊られて待つ高さ (足が床から px) と、装置の真ん中からの左右の位置 (px)。 */
  hangLift: 230, hangDx: 210,
  /** 下ろす・引き上げる長さ (ms)。 */
  placeMs: 450,
  /** 主人公から下ろす所 (px)。 */
  near: 190,
  /** 白い駒・鈴・投げられた体の速さ (px/秒)。 */
  pieceSpeed: 520, bellSpeed: 380, rideSpeed: 700,
  /** 振り子の弧の高さ (px)。 */
  swingArc: 120,
} as const
export type PuppetDo = 'place' | 'lift' | 'piece' | 'bell' | 'glow' | 'wave' | 'ride'
/** 吊られた二人への合図 (絶対時刻 ms)。place = 下ろす (x・lift へ ms かけて・arc は跳ねの高さ)・lift = 吊り上げる・
 *  piece / bell = 白い駒 / 鈴を主人公へ (dir があれば向き固定)・glow = 赤く光る (ms)・wave = 体の周り reach の赤い帯・
 *  ride = 体ごと床を走る (white = 弾ける振り子・でなければ赤の引きずり)。 */
export interface PuppetCue { at: number; by: TwinId; do: PuppetDo; x?: number; lift?: number; ms?: number; arc?: number; dir?: number; reach?: number; white?: boolean; done?: boolean }
export interface PuppetState {
  id: TwinId; x: number; lift: number
  tween: { fx: number; fl: number; tx: number; tl: number; start: number; ms: number; arc: number } | null
  ride: { hitId: number; from: number; to: number; start: number; ms: number; arc: number; white: boolean } | null
  redFrom: number; redUntil: number; thrustAt: number
}
export const PUPPET_ORDER: Record<TwinsPuppetPlan, DevKind[]> = {
  a: ['march', 'p_sen', 'wall', 'tsume', 'p_triple', 'curtain', 'p_order', 'map', 'light', 'tsume'],
  b: ['march', 'p_pendulum', 'wall', 'tsume', 'p_bounce', 'curtain', 'p_drag', 'map', 'light', 'tsume'],
  c: ['march', 'wall', 'tsume', 'curtain', 'map', 'light', 'tsume'],
}
/** 吊られた二人の技の名前 (記録・撮りの札用。ゲームには出さない)。 */
export const PUPPET_LABEL: Partial<Record<DevKind, string>> = {
  p_sen: '千日手（吊り）', p_triple: '三連打（吊り）', p_order: '号令（吊り）',
  p_pendulum: '振り子＝二段払い', p_bounce: '糸の跳ね＝三連打', p_drag: '引きずり＝滑り込み',
}
function hangX(id: TwinId, w: { mid: number }): number { return w.mid + (id === 'ane' ? -1 : 1) * PUPPET.hangDx }
function puppetsAtRest(w: { mid: number }): PuppetState[] {
  return (['ane', 'imo'] as TwinId[]).map((id) => ({ id, x: hangX(id, w), lift: PUPPET.hangLift, tween: null, ride: null, redFrom: 0, redUntil: 0, thrustAt: -1e9 }))
}
/** 吊られた二人の技の合図を作る (devBegin から)。返すのは合図と技の長さ (ms)。 */
function puppetMove(kind: DevKind, now: number, px: number, w: { left: number; right: number; mid: number }): { cues: PuppetCue[]; endMs: number } {
  const cx = (x: number) => Math.max(w.left, Math.min(w.right, x))
  const s = px < w.mid ? 1 : -1   // 主人公から見て広い側
  const c = (at: number, by: TwinId, d: PuppetDo, o: Partial<PuppetCue> = {}): PuppetCue => ({ at: now + at, by, do: d, ...o })
  switch (kind) {
    case 'p_sen': {
      // 千日手: 二人を主人公の両側へ下ろし、姉・妹・姉の白の後、妹の体ごとの赤 (1・2幕の締めの赤をなぞる)。
      const L = cx(px - PUPPET.near), R = cx(px + PUPPET.near)
      return { cues: [c(0, 'ane', 'place', { x: L, lift: 0 }), c(0, 'imo', 'place', { x: R, lift: 0 }),
        c(800, 'ane', 'piece'), c(1200, 'imo', 'piece'), c(1600, 'ane', 'piece'),
        c(2100, 'imo', 'glow', { ms: 450 }), c(2550, 'imo', 'ride', { white: false }),
        c(3500, 'ane', 'lift'), c(3700, 'imo', 'lift')], endMs: 4200 }
    }
    case 'p_triple': {
      const x = cx(px + s * PUPPET.near)
      return { cues: [c(0, 'imo', 'place', { x, lift: 0 }), c(700, 'imo', 'piece'), c(1100, 'imo', 'piece'), c(1500, 'imo', 'piece'), c(2300, 'imo', 'lift')], endMs: 2800 }
    }
    case 'p_order': {
      // 号令: 姉が主人公の近くへ下ろされ、赤く光ってから体の周りへ赤の波。
      const x = cx(px + s * 150)
      return { cues: [c(0, 'ane', 'place', { x, lift: 0 }), c(500, 'ane', 'glow', { ms: 850 }), c(1350, 'ane', 'wave', { reach: 300 }), c(2100, 'ane', 'lift')], endMs: 2600 }
    }
    case 'p_pendulum': {
      // 二段払い → 振り子: 姉が糸で部屋の端から端へ振れ、戻ってくる。どちらも白 (弾くと装置へ返り、姉は引き上げられる)。
      const a = s > 0 ? w.right : w.left, b = s > 0 ? w.left : w.right
      return { cues: [c(0, 'ane', 'place', { x: a, lift: PUPPET.swingArc }), c(700, 'ane', 'ride', { white: true, x: b }),
        c(2100, 'ane', 'place', { x: b, lift: PUPPET.swingArc, ms: 300 }), c(2500, 'ane', 'ride', { white: true, x: a }),
        c(4000, 'ane', 'lift')], endMs: 4400 }
    }
    case 'p_bounce': {
      // 三連打 → 糸の跳ね: 妹が糸で跳ねながら、主人公の左右へ交互に下りて白の駒を1つずつ。
      const A = cx(px + s * PUPPET.near), B = cx(px - s * PUPPET.near)
      return { cues: [c(0, 'imo', 'place', { x: A, lift: 0 }), c(650, 'imo', 'piece'),
        c(850, 'imo', 'place', { x: B, lift: 0, arc: 200, ms: 420 }), c(1500, 'imo', 'piece'),
        c(1700, 'imo', 'place', { x: A, lift: 0, arc: 200, ms: 420 }), c(2350, 'imo', 'piece'),
        c(3000, 'imo', 'lift')], endMs: 3500 }
    }
    case 'p_drag': {
      // 滑り込み → 引きずり: 妹が遠い壁際へ下ろされ、赤く光ってから床を壁から壁まで引きずられる (避ける)。
      const a = s > 0 ? w.right : w.left
      return { cues: [c(0, 'imo', 'place', { x: a, lift: 0 }), c(500, 'imo', 'glow', { ms: 800 }), c(1300, 'imo', 'ride', { white: false }), c(2700, 'imo', 'lift')], endMs: 3200 }
    }
    default: return { cues: [], endMs: 0 }
  }
}
/** 案 c: 装置の床の技の戻りに、二人の小技を差し込む (床を叩いた時刻から)。 */
function puppetFollow(kind: DevKind, now: number, px: number, d: DevAtk, w: { left: number; right: number; mid: number }): PuppetCue[] {
  const cx = (x: number) => Math.max(w.left, Math.min(w.right, x))
  const s = px < w.mid ? 1 : -1
  const c = (at: number, by: TwinId, dd: PuppetDo, o: Partial<PuppetCue> = {}): PuppetCue => ({ at: now + at, by, do: dd, ...o })
  if (kind === 'wall') {
    // 守りの駒の壁 → 妹の三連打 (壁と反対の側から)。
    const x = cx(px + (d.x < px ? 1 : -1) * PUPPET.near)
    return [c(0, 'imo', 'place', { x, lift: 0 }), c(600, 'imo', 'piece'), c(1000, 'imo', 'piece'), c(1400, 'imo', 'piece'), c(2100, 'imo', 'lift')]
  }
  if (kind === 'map') {
    // 城の地図 (床の半分) → 安全な半分で姉が待ち、踏み込み突き。
    const x = cx(mapEdgeX(d, w) - d.side * 260)
    return [c(-500, 'ane', 'place', { x, lift: 0 }), c(500, 'ane', 'piece'), c(1500, 'ane', 'lift')]
  }
  if (kind === 'curtain') {
    // 緞帳 → 千日手の締め (姉の白・妹の白・妹の赤)。
    const L = cx(px - PUPPET.near), R = cx(px + PUPPET.near)
    return [c(0, 'ane', 'place', { x: L, lift: 0 }), c(0, 'imo', 'place', { x: R, lift: 0 }), c(550, 'ane', 'piece'), c(950, 'imo', 'piece'),
      c(1350, 'imo', 'glow', { ms: 450 }), c(1800, 'imo', 'ride', { white: false }), c(2700, 'ane', 'lift'), c(2900, 'imo', 'lift')]
  }
  if (kind === 'light') {
    // 読みの照明 → 遠くから妹の鈴の弾を2つ。
    const x = cx(px + s * 380)
    return [c(-400, 'imo', 'place', { x, lift: 0 }), c(400, 'imo', 'bell'), c(1000, 'imo', 'bell'), c(1700, 'imo', 'lift')]
  }
  return []
}
/** 吊られた二人の今の位置 (描画とテスト)。 */
export function twinsPuppets(): PuppetState[] { return fight?.puppets ?? [] }

/** 弾き返した鈴が一発で削る体力 (hits.ts REFLECTED_PROJECTILE_BOSS_HIT_DRAFT.damage と同じ)。案A ではこの分を戻す。 */
const REFLECTED_BELL_HP = 6

export interface TwinState {
  id: TwinId; x: number; face: 1 | -1
  pool: number; lost: number; down: boolean
  /** 案A: 倒れた一人が起き上がる時刻 (ms)。0 = 起き上がらない (配布版)。 */
  downUntil: number
  /** 案A: 起き上がりが終わる時刻 (ms)。0 = 起き上がりの途中ではない。 */
  riseUntil: number
  move: TwinMove | null; start: number; booked: BookedHit[]; done: Set<number>; nextThink: number
  fromX: number; lunge: number; crossing: boolean; told: boolean
  /** 反対側へ回る時の跳び越え (主人公の頭の上を弧で越える。体を通り抜けて重ならないように・10-30)。 */
  hop: { from: number; start: number; dur: number } | null
}
export type DevKind = 'march' | 'wall' | 'map' | 'curtain' | 'light' | 'tsume'
  /** 吊られた二人の技 (`&twinsPuppet=a|b`)。 */
  | 'p_sen' | 'p_triple' | 'p_order' | 'p_pendulum' | 'p_bounce' | 'p_drag'
export const DEV_ORDER: DevKind[] = ['march', 'wall', 'map', 'curtain', 'light']
/** 打ち返す版の順 (詰めろを2回挟む)。 */
export const DEV_ORDER_RETURN: DevKind[] = ['march', 'wall', 'tsume', 'curtain', 'map', 'light', 'tsume']
export interface DevPiece { x: number; out: number; hitId: number; spawned: boolean }
export interface DevAtk {
  kind: DevKind; start: number; end: number; side: -1 | 1; x: number; hit: boolean
  pieces: DevPiece[]; wallHp: number; wallUntil: number; lightX: number
  /** 打ち返す版: これから出す白い駒 (出す時刻 ms・突きは出る所と向きも決めてある)。 */
  rets: { at: number; x?: number; dir?: number }[]
  /** 詰めろ: 安全なマスの番号 (0..7)。 */
  safe: number
  /** 吊られた二人への合図 (`&twinsPuppet`)。 */
  cues?: PuppetCue[]
}
export interface TwinsFight {
  room: 1 | 2 | 3; roomKey: number; lastNow: number
  twins: TwinState[]
  brokenUntil: number
  /** 案A: 総崩れが終わる時刻 (ms)。0 = 総崩れではない。 */
  collapseUntil: number
  /** 案A: 前のコマに飛んでいた弾き返した鈴 (id → x)。消えた鈴がボスに当たったかを数える。 */
  bells: Map<number, number>
  devPool: number; devLost: number; dev: DevAtk | null; devNext: number; devIdx: number
  pending: ActiveHit[]
  /** 弾いた時に装置を削る行軍の駒の当たり (id)。 */
  marchIds: Set<number>
  /** いま斬れる所 (boss.x を置く先)。null = 斬れる所が無い。 */
  target: { kind: 'twin'; id: TwinId } | { kind: 'wall' | 'map' | 'curtain' | 'light' | 'falter' } | null
  /** 案A の3部屋目: 部品の残りの太刀と、壊れた時刻 (ms・0 = 壊れていない)。 */
  parts: Record<PartKind, { hp: number; brokenAt: number }>
  /** 打ち返す版: 飛んでいる白い駒 (id)・弾いて当たりを消す駒 (id)・装置へ戻っている駒・よろめきの終わりと斬れる所。 */
  returnIds: Set<number>; returned: Set<number>
  flying: { x: number; start: number }[]
  /** 最後に装置へ当たった時刻と部品 (描画の光と揺れ)。 */
  lastReturn: { at: number; part: PartKind | null } | null
  falterUntil: number; falterX: number
  /** 吊られた二人 (`&twinsPuppet` の3部屋目だけ・ほかは空)。技の途中で駒が装置へ着いた時は、よろめきを技の終わりまで待たせる (x)。 */
  puppets: PuppetState[]; falterQueued: number | null
  /** 部品を削った技 (吊られた二人の技では、1つの技で削る部品は1回まで)。 */
  partDev?: DevAtk | null
  /** 3部屋目の現れ (2部屋目の致命の後だけ)。倒れた二人から光が浮かび、画面の中央へ集まって、装置が突然現れる。 */
  reveal: { start: number; bodies: { id: TwinId; x: number; face: 1 | -1 }[] } | null
}
let fight: TwinsFight | null = null
export function twinsFightState(): TwinsFight | null { return fight }

let nextId = 700000
// 技の選び・考える間・壁の位置の乱数。 テストは種のある乱数に差し替えて、 毎回同じ流れで測る (11-01・全体テストの時だけ位置取りのテストが揺れたため)。
let rnd: () => number = Math.random
export function setTwinsRandom(r: (() => number) | null): void { rnd = r ?? Math.random }

function walls(boss: Boss) {
  const w = arenaBoundsFor(boss.id)
  return { left: w.leftWall + 40, right: w.rightWall - 40, mid: (w.leftWall + w.rightWall) / 2, width: w.rightWall - w.leftWall }
}

function roomOf(boss: Boss): 1 | 2 | 3 {
  return boss.executesTaken <= 0 ? 1 : boss.executesTaken === 1 ? 2 : 3
}

/** 3部屋目の現れの時間割 (ms・10-30 作者「2幕のちめいをいれてから光を浮上させた後、画面中央に光が集中して突如として3幕目が現れる」)。
 *  倒れた二人を残す → 体から光が浮かび体が薄れる → 光が画面の中央へ集まる → 装置が突然現れる → 光が消える。 */
export const REVEAL = { rise: 700, gather: 1900, appear: 2700, end: 3400 } as const
/** 3部屋目の現れの比較版 C「上から落ちる重さ」(10-30 夜 作者が答えるページで C を選んだ・開発版 ?twinsReveal=c)。
 *  部屋が暗くなる → 倒れた二人から光が浮かぶ → 光が装置の真上の一点へ吸い込まれて空気がゆがむ → 光が消え床に影が広がる
 *  → 装置が真上から落ちて床を割る (appear) → 灰と砂ぼこりが吹き荒れて、暗さが引く (end)。 */
const REVEAL_DROP_BASE = { dark: 200, rise: 400, gather: 1500, swallow: 2150, fall: 2450, appear: 2680, end: 4600 } as const
/** 溜め (光が呑まれてから装置が落ち始めるまで) を伸ばした時は fall 以降がずれる。setTwinsRevealHold で書き換える。 */
export const REVEAL_DROP: { -readonly [K in keyof typeof REVEAL_DROP_BASE]: number } = { ...REVEAL_DROP_BASE }
/** 配布版の既定の溜め (11-01 作者が +1.0 秒を選んだ)。 */
export const REVEAL_HOLD_DEFAULT = 1000
/** C の溜めを伸ばす比較版 (10-31 作者「落ちてくるまでの間がもう少し欲しい」・開発版 &twinsRevealHold=300|600|1000 ms)。 */
export function setTwinsRevealHold(ms: number): void {
  const h = Number.isFinite(ms) ? Math.max(0, Math.min(2000, ms)) : 0
  REVEAL_DROP.fall = REVEAL_DROP_BASE.fall + h; REVEAL_DROP.appear = REVEAL_DROP_BASE.appear + h; REVEAL_DROP.end = REVEAL_DROP_BASE.end + h
}
setTwinsRevealHold(REVEAL_HOLD_DEFAULT)
export type TwinsRevealLook = 'light' | 'drop'
// 既定は C「上から落ちる重さ」(11-01 作者が選んだ)。前の「光が集まって現れる」版は開発版 ?twinsReveal=old で残す。
let revealLook: TwinsRevealLook = 'drop'
export function setTwinsRevealLook(v: string | null): void { revealLook = v === 'old' ? 'light' : 'drop' }
export function twinsRevealLook(): TwinsRevealLook { return revealLook }
/** 装置が現れる (落ちて床に着く) 時刻。 */
export function revealAppearMs(): number { return revealLook === 'drop' ? REVEAL_DROP.appear : REVEAL.appear }
let revealShakeAt: number | null = null
/** 落ちた装置が床を割った時の揺れ (1回だけ渡す・強くしすぎない)。game.ts が毎コマ聞く。 */
export function takeTwinsRevealShake(nowSec: number): ScreenShake | null {
  if (revealShakeAt === null) return null
  revealShakeAt = null
  return { remainingSec: 0.34, amplitude: 7, startAmplitude: 7, totalDur: 0.34, startSec: nowSec }
}

function setup(boss: Boss, player: Player, now: number, prev: TwinsFight | null): TwinsFight {
  const room = roomOf(boss)
  // 2部屋目の致命から続けて入った時だけ現れを見せる (開発用に部屋へ飛んだ時・やり直しは装置をすぐ出す)。
  const reveal = room === 3 && prev?.room === 2 && prev.roomKey === boss.executesTaken - 1
    ? { start: now, bodies: prev.twins.map((t) => ({ id: t.id, x: t.x, face: t.face })) } : null
  const span = Math.max(1, roomSize(boss) - chipFloor(boss))
  const w = walls(boss)
  const mk = (id: TwinId, x: number): TwinState => ({
    id, x, face: 1, pool: span / 2, lost: 0, down: false, downUntil: 0, riseUntil: 0, move: null, start: 0, booked: [], done: new Set(),
    nextThink: now + 900 + (id === 'imo' ? 500 : 0), fromX: x, lunge: 0, crossing: false, told: false, hop: null,
  })
  const left = Math.max(w.left, player.x - 260), right = Math.min(w.right, player.x + 260)
  return {
    room, roomKey: boss.executesTaken, lastNow: now,
    twins: room === 3 ? [] : [mk('ane', left), mk('imo', right)],
    brokenUntil: 0, collapseUntil: 0, bells: new Map(), devPool: span, devLost: 0, dev: null, devNext: now + (reveal ? revealAppearMs() : 0) + 1200, devIdx: 0,
    pending: [], marchIds: new Set(), target: null, reveal,
    parts: returnMode()
      ? { wall: { hp: RETURN.partHits, brokenAt: 0 }, curtain: { hp: RETURN.partHits, brokenAt: 0 }, light: { hp: RETURN.partHits, brokenAt: 0 } }
      : { wall: { hp: PLAN_A_PARTS.wall.hits, brokenAt: 0 }, curtain: { hp: PLAN_A_PARTS.curtain.hits, brokenAt: 0 }, light: { hp: PLAN_A_PARTS.light.hits, brokenAt: 0 } },
    returnIds: new Set(), returned: new Set(), flying: [], lastReturn: null, falterUntil: 0, falterX: 0,
    puppets: room === 3 && puppetMode() ? puppetsAtRest(w) : [], falterQueued: null,
  }
}

/** 部屋が変わった・やり直した時に作り直す。 */
function ensure(boss: Boss, player: Player, now: number): TwinsFight {
  // やり直し (時刻が戻る・hp が満タンへ戻る) も作り直す。
  const refilled = !!fight && fight.roomKey === boss.executesTaken && boss.hp > chipFloor(boss) + remainOf(fight) + 1
  if (!fight || fight.roomKey !== boss.executesTaken || now < fight.lastNow || refilled) fight = setup(boss, player, now, fight && !refilled && now >= fight.lastNow ? fight : null)
  return fight
}

/** 描く前に呼ぶ。名乗りの間 (まだ一度も動いていない) も二人を立たせ、前の総集編の絵を出さない。 */
export function twinsPrepare(boss: Boss, player: Player, nowSec: number): TwinsFight { return ensure(boss, player, nowSec * 1000) }

function remainOf(f: TwinsFight): number {
  return f.room === 3 ? f.devPool - f.devLost : f.twins.reduce((s, t) => s + (t.pool - t.lost), 0)
}

const tempo = (f: TwinsFight) => (f.room === 2 ? ROOM2_TEMPO : 1)
const at = (f: TwinsFight, ms: number) => ms * tempo(f)

/** 生まれた時の帯で当てる当たり。swing = 二人の体の振り (ガード・回避の反撃は他のボスの近接と同じ・11-03)。装置の床の技は swing を付けない (位置取りで避ける物)。 */
function band(attackId: string, x0: number, x1: number, parryable: boolean, damage: number, activeMs: number, originX: number, swing: boolean): ActiveHit {
  return {
    id: nextId++, attackId, kind: 'melee', ...(swing ? { swing: true as const } : {}), xStart: Math.min(x0, x1), xEnd: Math.max(x0, x1),
    elapsedMs: 0, telegraphMs: 0, activeMs, damage, postureDamage: parryable ? 10 : 0, parryable,
    hitOnce: true, consumed: false, playerWasHit: false, counterAwarded: false, parryIntensity: parryable ? 0.5 : 0.8,
    originX, originDir: 1,
  }
}

function bullet(attackId: string, x: number, dir: number, y: number, parryable: boolean, damage: number, speed: number, width: number): ActiveHit {
  return {
    id: nextId++, attackId, kind: 'melee', follow: 'travel', x, y, vx: dir * speed, vy: 0, widthPx: width, launchDelayMs: 0,
    xStart: x - width / 2, xEnd: x + width / 2, elapsedMs: 0, telegraphMs: 0, activeMs: 6000,
    damage, postureDamage: parryable ? 8 : 0, parryable,
    hitOnce: true, consumed: false, playerWasHit: false, counterAwarded: false, parryIntensity: 0.4,
  }
}

// ---- 二人 ----
/** 片方がいま赤く光っているか (赤い当たりの前の決まった間だけ・合わせ技では相方の弾く当たりの後から・redGlowFrom)。絵 (twins_fight_draw) とテストが同じ物を見る。 */
export function twinGlowsRed(t: TwinState, nowMs: number): boolean {
  const m = t.move
  if (!m || t.down) return false
  const el = nowMs - t.start
  const i = m.hits.findIndex((x, j) => x.by === t.id && !t.done.has(j))
  if (i < 0 || el < 0 || el >= m.hits[i]!.at) return false
  const from = redGlowFrom(m, i)
  return from !== null && el >= from
}

function partner(f: TwinsFight, t: TwinState): TwinState { return f.twins.find((x) => x !== t)! }

function begin(t: TwinState, m: TwinMove, start: number, dist: number): void {
  t.move = m; t.start = start; t.done = new Set(); t.fromX = t.x; t.told = false
  t.hop = null; t.crossing = false   // 合わせ技で相方の跳び越えの途中に始まった時も、技の後に古い跳びを再開しない
  t.lunge = m.lungePx ? Math.max(0, Math.min(m.lungePx, dist - 70)) : 0
  // 前の技で撃った鈴がまだ飛んでいる間は、その当たりの予約を残す (相方が赤い技をそこへ重ねないように・決まり5・11-03)。
  const flying = t.booked.filter((x) => x.from !== undefined && x.defense === 'parry' && x.t > start - SAME_BEAT_MS)
  // m は think で縮めた写し (2部屋目)。ここでもう一度縮めると予約が実際の当たりより早くずれる (11-03 直し)。
  t.booked = [...flying, ...absHits(m, start, dist).filter((x) => x.by === t.id)]
}

/** 2部屋目は技の時間を縮めた写しを使う。 */
function scaled(f: TwinsFight, m: TwinMove): TwinMove {
  if (tempo(f) === 1) return m
  return { ...m, hits: m.hits.map((h) => ({ ...h, at: at(f, h.at) })), endMs: at(f, m.endMs) }
}

function think(f: TwinsFight, t: TwinState, player: Player, now: number, w: ReturnType<typeof walls>): boolean {
  const p = partner(f, t)
  const partnerBig = !!(p.move && p.move.big && now < p.start + p.move.endMs)
  const dist = Math.abs(player.x - t.x)
  const c = pickable(t.id, partnerBig, p.down)
    .filter((m) => m.owner !== 'both' || t.id === 'ane')
    .filter((m) => dist <= m.needDx)
    .filter((m) => m.id !== 'i_bell' || dist > 160)
  if (c.length === 0) return false
  // 二人が同じ側にいる間は殴らない (挟み直してから)。壁際では挟めないので、その時だけは同じ側からでも殴る。
  if (!p.down && Math.sign(t.x - player.x) === Math.sign(p.x - player.x) && player.x > w.left + 110 && player.x < w.right - 110) return false
  const m = scaled(f, c[Math.floor(rnd() * c.length)]!)
  const partnerHits = p.booked.filter((x) => x.t > now - 300)
  const partnerBigUntil = partnerBig ? p.start + p.move!.endMs : 0
  const start = earliestStart(m, now, partnerHits, partnerBigUntil, dist)
  begin(t, m, start, dist)
  if (m.owner === 'both') begin(p, m, start, dist)
  return true
}

function twinTick(f: TwinsFight, t: TwinState, player: Player, now: number, dt: number, w: ReturnType<typeof walls>, sfx: SfxId[]): void {
  // 倒れ・起き上がり・総崩れで止まった跳び越えは捨てる (古い始まりの時刻のまま再開すると、最初のコマで行き先へ一気に移る)。
  if (t.down || twinRising(t, now)) { t.hop = null; t.crossing = false; return }
  t.face = player.x >= t.x ? 1 : -1
  if (now < f.brokenUntil) { t.move = null; t.booked = []; t.hop = null; t.crossing = false; return }
  const m = t.move
  if (m) {
    const el = now - t.start
    if (el < 0) return
    if (!t.told) {
      t.told = true
      // ?bossSfx=v1: 鈴・号令・滑り・千日手は固有の予告 (二人で出す技は二人とも鳴らす・同じ音は鳴り分けの間隔で1つに)。
      sfx.push(twinsSfxTell(m.id) ?? (m.hits.some((x) => x.by === t.id && x.defense === 'dodge') ? 'telegraph_start' : 'step'))
    }
    if (m.lungePx) t.x = t.fromX + t.face * t.lunge * Math.min(1, el / Math.max(1, m.hits[0]!.at - 100))
    if (m.slide) {
      const hitAt = m.hits[0]!.at, dir = t.fromX < player.x ? 1 : -1
      if (el > hitAt - 350) {
        const k = Math.min(1, (el - (hitAt - 350)) / 600)
        t.x = Math.max(w.left, Math.min(w.right, t.fromX + dir * k * (Math.abs(player.x - t.fromX) + 170)))
      }
    }
    if (m.owner === 'both') {
      const ane = f.twins[0]!
      const side = t.id === 'ane' ? (ane.fromX < player.x ? -1 : 1) : (ane.fromX < player.x ? 1 : -1)
      const want = Math.max(w.left, Math.min(w.right, player.x + side * 95))
      t.x += (want - t.x) * Math.min(1, dt / 160)
    }
    m.hits.forEach((x, i) => {
      if (x.by !== t.id || t.done.has(i) || el < x.at) return
      t.done.add(i)
      const parry = x.defense === 'parry'
      const id = `twins_${m.id}`
      const hitSfx = twinsSfxHit(m.id, x.defense)
      if (hitSfx) sfx.push(hitSfx)
      if (x.kind === 'bullet') { f.pending.push(bullet(id, t.x + t.face * 30, t.face, ARENA.floorY - 90, true, DMG.bullet, BULLET_SPEED, 24)); return }
      if (x.kind === 'wave') { f.pending.push(band(id, t.x - x.reach, t.x + x.reach, false, DMG.dodge, 200, t.x, true)); return }
      if (x.kind === 'pass') { f.pending.push(band(id, t.x - x.reach, t.x + x.reach, false, DMG.dodge, 260, t.x, true)); return }
      // 近接: 体の少し後ろから前へ reach まで (向きのある帯)
      f.pending.push(band(id, t.x - t.face * 20, t.x + t.face * x.reach, parry, parry ? DMG.parry : DMG.dodge, 130, t.x, true))
    })
    if (el >= m.endMs) { t.move = null; t.booked = t.booked.filter((x) => x.t > now); t.nextThink = now + at(f, 350 + rnd() * 500) }
    return
  }
  // 立ち位置: 主人公を二人で左右から挟む。同じ側に寄ったら、手の空いている方が主人公を跳び越えて反対側へ回る。
  //   重ならないように (10-30 作者「位置取りを注意しないと重なって…読みにくくなりそう」):
  //   - 回る時は主人公の頭の上を弧で越える (体を通り抜けない)。
  //   - 壁際で反対側に余地が無い時は回らず、同じ側に間を空けて並ぶ (近い方 100・遠い方 230)。
  //   - 相方が倒れている時は、その体から TWIN_GAP 以上離れて立つ。
  const p = partner(f, t)
  const room = (s: number) => (s < 0 ? player.x - w.left : w.right - player.x)
  const flip = (s: -1 | 1): -1 | 1 => (s === 1 ? -1 : 1)
  const sideOf = (x: number): -1 | 1 => (Math.abs(x - player.x) < 1 ? (room(-1) > room(1) ? -1 : 1) : x < player.x ? -1 : 1)
  const mySide = sideOf(t.x), pSide = sideOf(p.x)
  const pBusy = !!(p.move && now >= p.start)
  let side = mySide, dist = 100
  if (t.hop) side = player.x > t.hop.from ? 1 : -1
  else if (room(mySide) < 70) side = flip(mySide)   // 主人公と壁の間に挟まった: 跳び越えて広い側へ出る
  else if (!p.down && mySide === pSide) {
    const iCross = pBusy || t.id === 'imo' || p.crossing
    if (room(-pSide) < 90) {
      // 壁を背にした主人公: 同じ側で前後に並ぶ。主人公に近い方が前 (100)、後ろは前の者の 130 外側 (追い越さない)。
      const pd = Math.abs(p.x - player.x), td = Math.abs(t.x - player.x)
      if (td > pd || (td === pd && t.id === 'imo')) dist = Math.max(230, pd + 130)
    } else if (iCross && !p.crossing) side = flip(pSide)
  }
  if (t.id === 'imo' && pBusy && dist === 100) dist = 250
  let want = Math.max(w.left, Math.min(w.right, player.x + side * dist))
  if (p.down && Math.abs(want - p.x) < TWIN_GAP) {
    // 倒れた相方の上に立たない: 反対側に余地があればそちらへ、無ければ体の外側へ。
    const other = Math.max(w.left, Math.min(w.right, player.x - side * dist))
    want = Math.abs(other - p.x) >= TWIN_GAP && Math.abs(other - player.x) >= 60 ? other
      : Math.max(w.left, Math.min(w.right, p.x + (want >= p.x ? 1 : -1) * TWIN_GAP))
    if (Math.abs(want - p.x) < TWIN_GAP) want = Math.max(w.left, Math.min(w.right, p.x - (want >= p.x ? 1 : -1) * TWIN_GAP))
  }
  if (!t.hop && side !== mySide) t.hop = { from: t.x, start: now, dur: Math.max(HOP_MIN_MS, Math.abs(want - t.x) / HOP_SPEED * 1000) }
  if (t.hop) {
    const u = Math.min(1, (now - t.hop.start) / t.hop.dur)
    // 跳んでいる途中で行き先が変わっても (相方が倒れた・主人公が動いた) 1コマで飛ばない。弧の一番速い所 (平均の 1.5 倍) までに抑え、
    //   届かなかった分は着いた後の歩きで詰める (11-03 駅6 の点検で、姉が倒れたコマに妹が 108px 動いた)。
    const to = t.hop.from + (want - t.hop.from) * (u * u * (3 - 2 * u)), cap = HOP_SPEED * 1.5 * dt / 1000
    t.x += Math.max(-cap, Math.min(cap, to - t.x))
    t.crossing = true
    if (u >= 1) { t.hop = null; t.crossing = false }
    return
  }
  t.crossing = false
  // 技の終わりに主人公・相方へ重なって残った時 (壁際の滑り込み・突きの後) や、倒れた相方の上にいる時は、素早く間を空ける。
  const close = Math.abs(t.x - player.x) < 60 || Math.abs(t.x - p.x) < (p.down ? TWIN_GAP : 60)
  const step = (close ? 420 : 150) * dt / 1000
  if (Math.abs(want - t.x) > 6) t.x += Math.sign(want - t.x) * Math.min(step, Math.abs(want - t.x))
  if (now >= t.nextThink && !think(f, t, player, now, w)) t.nextThink = now + 200
}

// ---- 奥の装置 (3部屋目) ----
export function curtainSpan(d: DevAtk, w: { left: number; right: number }): [number, number] {
  const width = 420
  const x0 = Math.max(w.left, Math.min(w.right - width, d.x - width / 2))
  return [x0, x0 + width]
}
export function mapSpan(d: DevAtk, w: { mid: number; left: number; right: number }): [number, number] {
  return d.side < 0 ? [w.left - 40, w.mid + 80] : [w.mid - 80, w.right + 40]
}
export function mapEdgeX(d: DevAtk, w: { mid: number }): number { return d.side < 0 ? w.mid + 80 : w.mid - 80 }

function devBegin(f: TwinsFight, kind: DevKind, player: Player, now: number, w: ReturnType<typeof walls>): void {
  const side: -1 | 1 = player.x < w.mid ? -1 : 1
  const d: DevAtk = { kind, start: now, end: now + 3000, side, x: player.x, hit: false, pieces: [], wallHp: planA && !returnMode() ? f.parts.wall.hp : 3, wallUntil: 0, lightX: player.x, rets: [], safe: 0, cues: [] }
  if (kind === 'march') {
    const from: -1 | 1 = player.x < w.mid ? 1 : -1   // 遠い側の端から出てくる
    d.side = from
    for (let i = 0; i < 3; i++) d.pieces.push({ x: from > 0 ? w.right + 80 : w.left - 80, out: 900 + i * 520, hitId: 0, spawned: false })
    d.end = now + 5200
  }
  if (kind === 'wall') { d.x = Math.max(w.left + 30, Math.min(w.right - 30, player.x + (rnd() < 0.5 ? -1 : 1) * 120)); d.end = now + 5600; d.wallUntil = now + 5600 }
  if (kind === 'map') d.end = now + 3600
  if (kind === 'curtain') d.end = now + 2800
  if (kind === 'light') d.end = now + 3100
  if (kind === 'tsume') {
    d.safe = tsumeSafeCell(player.x, w, rnd)
    const [a, b] = tsumeSpan(d, w), c = (a + b) / 2
    const first: -1 | 1 = rnd() < 0.5 ? -1 : 1
    d.rets = TSUME.thrustAt.map((ms, i) => {
      const s = i % 2 ? -first : first
      return { at: now + ms, x: c + s * ((b - a) / 2 + TSUME.thrustGap), dir: -s }
    })
    d.x = c
    d.end = now + TSUME.endMs
    if (puppetMode()) {
      // 突きは下ろされた二人が出す (先に出る方 = 姉)。
      d.cues = d.rets.flatMap((r, i): PuppetCue[] => {
        const by: TwinId = i === 0 ? 'ane' : 'imo'
        return [{ at: r.at - 700, by, do: 'place', x: r.x!, lift: 0 }, { at: r.at, by, do: 'piece', dir: r.dir! }, { at: now + TSUME.endMs - 300, by, do: 'lift' }]
      })
      d.rets = []
    }
  }
  if (kind.startsWith('p_')) {
    const pm = puppetMove(kind, now, player.x, w)
    d.cues = pm.cues; d.end = now + pm.endMs
  }
  f.dev = d
}

/** 次の装置の技。案A では壊れた部品の技を飛ばす (攻めの駒の行軍と城の地図は壊れないので、必ず何かが出る)。 */
function nextDevKind(f: TwinsFight): DevKind {
  const order = puppetMode() ? PUPPET_ORDER[puppetPlan!] : returnMode() ? DEV_ORDER_RETURN : DEV_ORDER
  for (let i = 0; i < order.length; i++) {
    const k = order[f.devIdx++ % order.length]!
    if (!planA || !(k in f.parts) || !f.parts[k as PartKind].brokenAt) return k
  }
  return 'march'
}

function devTick(f: TwinsFight, player: Player, now: number, dt: number, w: ReturnType<typeof walls>, sfx: SfxId[]): void {
  if (f.devLost >= f.devPool) return
  if (now < f.brokenUntil || now < f.falterUntil || !f.dev) for (const p of f.puppets) puppetMoveTick(p, now)
  if (now < f.brokenUntil || now < f.falterUntil) return
  if (!f.dev) {
    if (now >= f.devNext) { devBegin(f, nextDevKind(f), player, now, w); sfx.push(twinsSfxDevice('start') ?? 'telegraph_start') }
    return
  }
  const d = f.dev, el = now - d.start
  if (d.kind === 'march') {
    for (const p of d.pieces) {
      if (p.spawned || el < p.out) continue
      p.spawned = true
      const h = bullet('twins_dev_march', p.x, -d.side, ARENA.floorY - 40, true, DMG.parry, 380, 50)
      p.hitId = h.id; f.marchIds.add(h.id); f.pending.push(h)
      const marchSfx = twinsSfxDevice('march')
      if (marchSfx) sfx.push(marchSfx)
    }
  }
  const slam = (x0: number, x1: number, origin: number, activeMs = 160) => {
    d.hit = true; sfx.push(twinsSfxDevice('slam') ?? 'attack_swing_heavy'); f.pending.push(band(`twins_dev_${d.kind}`, x0, x1, false, DMG.device, activeMs, origin, false))
    if (puppetPlan === 'c' && puppetMode() && d.kind !== 'tsume') {
      // 案 c: 白い駒の代わりに二人の小技が戻りを埋める。技の長さを合図の後ろまで伸ばす。
      d.cues = [...(d.cues ?? []), ...puppetFollow(d.kind, now, player.x, d, w)]
      d.end = Math.max(d.end, ...d.cues.map((x) => x.at + 400))
    } else if (returnMode() && d.kind !== 'tsume') d.rets.push({ at: now + RETURN.launchMs })
  }
  if (d.kind === 'wall' && !d.hit && el >= DEV_SLAM_MS.wall) slam(d.x - 52, d.x + 52, d.x)
  if (d.kind === 'map' && !d.hit && el >= DEV_SLAM_MS.map) { const [a, b] = mapSpan(d, w); slam(a, b, (a + b) / 2) }
  if (d.kind === 'curtain' && !d.hit && el >= DEV_SLAM_MS.curtain) { const [a, b] = curtainSpan(d, w); slam(a, b, (a + b) / 2) }
  if (d.kind === 'light') {
    if (el < 1600) d.lightX += (player.x - d.lightX) * Math.min(1, dt / 330)
    if (!d.hit && el >= DEV_SLAM_MS.light) slam(d.lightX - 58, d.lightX + 58, d.lightX)
  }
  if (d.kind === 'tsume' && !d.hit && el >= TSUME.tellMs) {
    const [a, b] = tsumeSpan(d, w)
    // 外のマスへ一斉に。落ちた駒は暫く床に残る (外へ出ると当たる)。安全なマスが壁際の時は片側だけ。
    if (a > w.left + 1) slam(w.left - 40, a, (w.left + a) / 2, TSUME.holdMs)
    if (b < w.right - 1) { if (d.hit) f.pending.push(band('twins_dev_tsume', b, w.right + 40, false, DMG.device, TSUME.holdMs, (b + w.right) / 2, false)); else slam(b, w.right + 40, (b + w.right) / 2, TSUME.holdMs) }
  }
  if (returnMode()) {
    for (let i = d.rets.length - 1; i >= 0; i--) {
      const r = d.rets[i]!
      if (now < r.at) continue
      d.rets.splice(i, 1)
      // 白い駒: 主人公の広い側から主人公へ (突きは決めた所から安全なマスへ)。
      const s = player.x < w.mid ? 1 : -1
      const x = r.x ?? Math.max(w.left - 20, Math.min(w.right + 20, player.x + s * RETURN.launchGap))
      const dir = r.dir ?? -s
      const h = bullet('twins_dev_return', x, dir, ARENA.floorY - 60, true, DMG.parry, d.kind === 'tsume' ? TSUME.thrustSpeed : RETURN.speed, 44)
      f.returnIds.add(h.id); f.pending.push(h)
      sfx.push(twinsSfxDevice('march') ?? 'telegraph_start')
    }
  }
  if (d.cues?.length) puppetCues(f, d, player, now, w, sfx)
  else for (const p of f.puppets) puppetMoveTick(p, now)
  if (now >= d.end && d.rets.length === 0 && (d.cues ?? []).every((x) => x.done) && el >= (d.kind === 'march' ? 900 + 2 * 520 + 2500 : 0)) {
    f.dev = null; f.devNext = now + 1100
    // 技の途中で駒が装置へ着いていたら、ここでよろめく。
    if (f.falterQueued !== null) startFalter(f, now, f.falterQueued, w)
  }
}

/** 吊られた二人への合図を時刻どおりに出す。 */
function puppetCues(f: TwinsFight, d: DevAtk, player: Player, now: number, w: ReturnType<typeof walls>, sfx: SfxId[]): void {
  for (const q of d.cues ?? []) {
    if (q.done || now < q.at) continue
    q.done = true
    const p = f.puppets.find((x) => x.id === q.by)
    if (!p) continue
    const toward = (): number => q.dir ?? (player.x >= p.x ? 1 : -1)
    if (q.do === 'place' || q.do === 'lift') {
      const tx = q.do === 'lift' ? hangX(p.id, w) : q.x ?? p.x, tl = q.do === 'lift' ? PUPPET.hangLift : q.lift ?? 0
      p.ride = null
      p.tween = { fx: p.x, fl: p.lift, tx, tl, start: now, ms: q.ms ?? PUPPET.placeMs, arc: q.arc ?? 0 }
      if (q.do === 'place') sfx.push('step')
    }
    if (q.do === 'piece' || q.do === 'bell') {
      const dir = toward(), bell = q.do === 'bell'
      const h = bullet('twins_dev_return', p.x + dir * 24, dir, ARENA.floorY - (bell ? 90 : 60), true, DMG.parry, bell ? PUPPET.bellSpeed : PUPPET.pieceSpeed, bell ? 30 : 44)
      f.returnIds.add(h.id); f.pending.push(h)
      p.thrustAt = now
      sfx.push(twinsSfxDevice('march') ?? 'telegraph_start')
    }
    if (q.do === 'glow') { p.redFrom = now; p.redUntil = now + (q.ms ?? 600); sfx.push('telegraph_start') }
    if (q.do === 'wave') {
      f.pending.push(band('twins_puppet_red', p.x - (q.reach ?? 300), p.x + (q.reach ?? 300), false, DMG.dodge, 200, p.x, false))
      sfx.push('attack_swing_heavy')
    }
    if (q.do === 'ride') {
      const dir = q.x !== undefined ? (q.x >= p.x ? 1 : -1) : toward()
      const to = q.x ?? (dir > 0 ? w.right : w.left)
      const white = !!q.white
      const h = bullet(white ? 'twins_puppet_ride' : 'twins_puppet_red', p.x, dir, ARENA.floorY - 60, white, white ? DMG.parry : DMG.dodge, PUPPET.rideSpeed, 56)
      if (white) f.returnIds.add(h.id)
      f.pending.push(h)
      p.tween = null
      p.ride = { hitId: h.id, from: p.x, to, start: now, ms: Math.abs(to - p.x) / PUPPET.rideSpeed * 1000, arc: white ? p.lift : 0, white }
      sfx.push(white ? 'attack_swing' : 'attack_swing_heavy')
    }
  }
  for (const p of f.puppets) puppetMoveTick(p, now)
}
/** 吊られた二人の位置を時刻から決める (下ろす・吊り上げる・振り子・引きずり)。 */
function puppetMoveTick(p: PuppetState, now: number): void {
  if (p.ride) {
    const u = Math.min(1, (now - p.ride.start) / Math.max(1, p.ride.ms))
    p.x = p.ride.from + (p.ride.to - p.ride.from) * u
    // 振り子は端で高く、真ん中で床すれすれ。
    p.lift = p.ride.arc * (1 - Math.sin(Math.PI * u))
    if (u >= 1) p.ride = null
    return
  }
  if (p.tween) {
    const t = p.tween, u = Math.min(1, (now - t.start) / Math.max(1, t.ms)), e = u * u * (3 - 2 * u)
    p.x = t.fx + (t.tx - t.fx) * e
    p.lift = t.fl + (t.tl - t.fl) * e + t.arc * Math.sin(Math.PI * u)
    if (u >= 1) p.tween = null
  }
}
/** 吊られた二人の姿勢の絵 (11-05)。hang＝吊られて待つ・hop＝糸で跳ねる・swing＝振り子・drag＝引きずり・thrust＝駒を突き出す。 */
export type PuppetPose = 'hang' | 'hop' | 'swing' | 'drag' | 'thrust'
/** 駒を突き出した姿勢を見せておく長さ (ms)。 */
export const PUPPET_THRUST_HOLD_MS = 420
/** 今の姿勢と、絵の向き (振り子・引きずりは進む向き。ほかは null＝主人公の方を向く)。 */
export function puppetPose(p: PuppetState, now: number): { pose: PuppetPose; face: 1 | -1 | null } {
  if (p.ride) {
    const face = p.ride.to >= p.ride.from ? 1 : -1
    return { pose: p.ride.white ? 'swing' : 'drag', face }
  }
  if (now - p.thrustAt >= 0 && now - p.thrustAt < PUPPET_THRUST_HOLD_MS) return { pose: 'thrust', face: null }
  if (p.tween && p.tween.arc > 0 && now - p.tween.start < p.tween.ms) return { pose: 'hop', face: p.tween.tx >= p.tween.fx ? 1 : -1 }
  return { pose: 'hang', face: null }
}
/** よろめきを始める (出ている技を止め、駒を弾いた所に斬れる所を出す)。 */
function startFalter(f: TwinsFight, now: number, x: number, w: ReturnType<typeof walls>): void {
  f.falterQueued = null
  f.falterUntil = now + RETURN.falterMs
  f.falterX = Math.max(w.left + 30, Math.min(w.right - 30, x))
  if (f.dev) f.dev = null
  f.devNext = f.falterUntil + 600
  // 吊られた二人は梁へ引き上げられる。
  for (const p of f.puppets) { p.ride = null; p.tween = { fx: p.x, fl: p.lift, tx: hangX(p.id, w), tl: PUPPET.hangLift, start: now, ms: PUPPET.placeMs, arc: 0 } }
}

/** 装置の床の技 (壁・地図・幕・照明) が床を叩くまでの時間 (技の始まりから ms)。 */
export const DEV_SLAM_MS: Record<'wall' | 'map' | 'curtain' | 'light' | 'tsume', number> = { wall: 1120, map: 1300, curtain: 1550, light: 2100, tsume: TSUME.tellMs }
/** 装置の床の技の赤い予告 (弾けない・ガードできない＝赤く光らせる・11-03 決まりの外れの直し)。
 *  技の始まりから床を叩くまで、叩く床の幅を返す。行軍の駒は弾ける当たりなので赤くしない (null)。 */
export function devRedSpan(d: DevAtk, w: { left: number; right: number; mid: number }, nowMs: number): [number, number] | null {
  // 詰めろは外のマス全部が赤い (描画は tsumeSpan で別に描く)。
  if (d.kind === 'march' || d.kind === 'tsume' || d.kind.startsWith('p_')) return null
  const el = nowMs - d.start
  if (el < 0 || el >= DEV_SLAM_MS[d.kind as keyof typeof DEV_SLAM_MS]) return null
  if (d.kind === 'wall') return [d.x - 52, d.x + 52]
  if (d.kind === 'map') return mapSpan(d, w)
  if (d.kind === 'curtain') return curtainSpan(d, w)
  return [d.lightX - 58, d.lightX + 58]
}

/** 案A: 緞帳の裾・照明の歩の駒が斬れる間 (技の始まりから ms)。裾は床を叩いてから上がり切る前まで、歩の駒は落ちてから消えるまで。 */
export const PART_WINDOW_MS = { curtain: [1600, 2300], light: [2100, 3000] } as const

/** 3部屋目で斬れる所 (落ちた守りの駒・叩きつけた地図の縁。案A では緞帳の裾と照明の歩の駒も)。 */
function devTarget(f: TwinsFight, now: number, w: ReturnType<typeof walls>, player: Player): { kind: 'wall' | 'map' | 'curtain' | 'light' | 'falter'; x: number } | null {
  // 打ち返す版: よろめきの間は、駒を弾いた所に装置の手が落ちて斬れる。
  if (returnMode() && now < f.falterUntil) return { kind: 'falter', x: f.falterX }
  const d = f.dev
  if (!d) return null
  const el = now - d.start
  if (d.kind === 'wall' && el >= 1120 && d.wallHp > 0 && now < d.wallUntil) return { kind: 'wall', x: d.x }
  if (d.kind === 'map' && el >= 1300 && el < 3500) return { kind: 'map', x: mapEdgeX(d, w) }
  if (!planA || returnMode()) return null
  if (d.kind === 'curtain' && !f.parts.curtain.brokenAt && el >= PART_WINDOW_MS.curtain[0] && el < PART_WINDOW_MS.curtain[1]) {
    const [a, b] = curtainSpan(d, w)
    return { kind: 'curtain', x: player.x < (a + b) / 2 ? a : b }
  }
  if (d.kind === 'light' && !f.parts.light.brokenAt && el >= PART_WINDOW_MS.light[0] && el < PART_WINDOW_MS.light[1]) return { kind: 'light', x: d.lightX }
  return null
}

const FAR_AWAY = -100000

/** このコマは updateBoss の代わりに二人を動かすか (崩れて致命を待つ・致命の間・撃破の後は updateBoss のまま)。 */
export function twinsDriveThisTick(boss: Boss): boolean {
  return twinsFightOn(boss) && boss.state !== 'staggered' && boss.hp > 0
}

/** 毎コマ、updateBoss の代わりに呼ぶ。二人 (装置) を動かし、boss を「いま斬れる所」へ置く。 */
export function twinsBossTick(boss: Boss, player: Player, nowSec: number, sfx: SfxId[]): Boss {
  const now = nowSec * 1000
  const f = ensure(boss, player, now)
  const dt = Math.max(0, Math.min(100, now - f.lastNow))
  if (f.reveal) {
    const prev = f.lastNow - f.reveal.start, e = now - f.reveal.start
    const cross = (ms: number): boolean => prev < ms && e >= ms
    // C の現れ方の音 (11-01・src/boss_sfx_v1.ts twinsSfxReveal)。山を合図 (swallow・appear) に合わせて先に鳴らし始める。
    const suck = revealLook === 'drop' ? twinsSfxReveal('suck') : null
    const land = revealLook === 'drop' ? twinsSfxReveal('land') : null
    if (suck && cross(REVEAL_DROP.swallow - TWINS_REVEAL_SFX_PEAK_MS.suck)) sfx.push(suck)
    if (land && cross(REVEAL_DROP.appear - TWINS_REVEAL_SFX_PEAK_MS.land)) sfx.push(land)
    // 装置が現れた瞬間。着地の音が無い時 (前の光の現れ・?twinsRevealSfx=off) は仮の重い一音。
    if (cross(revealAppearMs())) {
      if (!land) sfx.push('hit_boss_heavy')
      if (revealLook === 'drop') revealShakeAt = now
    }
  }
  f.lastNow = now
  const w = walls(boss)
  if (planA && f.room !== 3) planATick(f, now)
  for (const t of f.twins) twinTick(f, t, player, now, dt, w, sfx)
  if (f.room === 3) devTick(f, player, now, dt, w, sfx)
  return placeBoss(f, { ...boss, state: 'idle', currentAttack: null, currentString: null, stateElapsed: boss.stateElapsed + dt / 1000,
    postExecuteInvincMs: Math.max(0, boss.postExecuteInvincMs - dt) }, player, now, w)
}

/** 案A: 総崩れの終わり・倒れた一人の起き上がり。 */
function planATick(f: TwinsFight, now: number): void {
  if (f.collapseUntil > 0) {
    if (now < f.collapseUntil) return
    f.collapseUntil = 0
    for (const t of f.twins) rise(t, now)
    return
  }
  for (const t of f.twins) if (t.down && t.downUntil > 0 && now >= t.downUntil) rise(t, now)
}
function rise(t: TwinState, now: number): void {
  t.down = false; t.downUntil = 0; t.move = null; t.booked = []
  t.lost = Math.min(t.lost, t.pool * (1 - PLAN_A.riseBack))
  t.riseUntil = now + PLAN_A.riseMs
  t.nextThink = t.riseUntil + 400
}
/** 案A: 立ち上がりの途中か (動かず・斬れない)。 */
export function twinRising(t: TwinState, now: number): boolean { return now < t.riseUntil }
/** 案A: 倒れの終わりの震えの強さ 0..1 (描画用)。起き上がらない倒れ (配布版) は 0。 */
export function twinRiseShake(t: TwinState, now: number): number {
  if (!t.down || t.downUntil <= 0) return 0
  const left = t.downUntil - now
  return left > 0 && left <= PLAN_A.shakeMs ? 1 - left / PLAN_A.shakeMs * 0.5 : 0
}
/** 案A: 総崩れの始まりと終わり (ms・描画用)。総崩れでなければ null。 */
export function twinsCollapseSpan(nowSec: number): { from: number; until: number } | null {
  const f = fight
  if (!f || f.collapseUntil <= 0 || nowSec * 1000 >= f.collapseUntil) return null
  return { from: f.collapseUntil - PLAN_A.collapseMs, until: f.collapseUntil }
}
/** 案A の総崩れの間か (描画用)。 */
export function twinsCollapsed(nowSec: number): boolean { return !!fight && nowSec * 1000 < fight.collapseUntil }

function placeBoss(f: TwinsFight, boss: Boss, player: Player, now: number, w: ReturnType<typeof walls>): Boss {
  let x = FAR_AWAY
  f.target = null
  if (f.room === 3) {
    const tg = devTarget(f, now, w, player)
    if (tg) { x = tg.x; f.target = { kind: tg.kind } }
  } else {
    const stand = f.twins.filter((t) => !t.down && !twinRising(t, now))
    const up = stand.filter((t) => !t.hop).length ? stand.filter((t) => !t.hop) : stand
    const near = (up.length ? up : f.twins).reduce((a, b) => (Math.abs(a.x - player.x) <= Math.abs(b.x - player.x) ? a : b))
    x = near.x
    f.target = { kind: 'twin', id: near.id }
  }
  // 斬れる所が無い時は遠くへ (主人公の太刀が届かない)。見た目は描画が二人・装置を描くので影響しない。
  if (x === FAR_AWAY) x = player.x + (player.x < w.mid ? 1 : -1) * 4000
  return { ...boss, x, facing: player.x >= x ? 'right' : 'left' }
}

/** game.ts が `let hits = state.hits` の後に足す。 */
export function twinsTakeHits(): ActiveHit[] {
  if (!fight || fight.pending.length === 0) return []
  const out = fight.pending
  fight.pending = []
  return out
}

/**
 * 当たりを解いた後に呼ぶ。主人公の削りを「いま斬れる所」へ割り振り、hp・体幹・崩れ・膝つきを整える。
 *   prev = このコマの頭の boss (= 削られる前)。hits = 当たりを解いた後のヒット (行軍の駒を弾いたかを見る)。
 */
export function twinsAfterHits(prev: Boss, boss: Boss, player: Player, nowSec: number, hits: readonly ActiveHit[], staggerBoss: (b: Boss) => Boss, sfx?: SfxId[]): Boss {
  const f = fight
  if (!f || prev.state === 'staggered' || boss.hp <= 0 || f.roomKey !== boss.executesTaken) return boss
  const now = nowSec * 1000
  const w = walls(boss)
  const broken = now < f.brokenUntil
  const struck = boss.hp < prev.hp
  const floor = chipFloor(boss)
  if (planA && f.room !== 3) return planAAfterHits(f, prev, boss, player, now, hits, floor, staggerBoss, sfx)
  if (planA) return planADeviceAfterHits(f, prev, boss, player, now, hits, floor, staggerBoss, sfx)
  if (f.room === 3) {
    devWear(f, now, struck, hits)
    boss = { ...boss, hp: Math.max(floor, floor + f.devPool - f.devLost) }
  } else {
    if (struck && f.target?.kind === 'twin') {
      const id = f.target.id
      const t = f.twins.find((x) => x.id === id)!
      t.lost = Math.min(t.pool, t.lost + t.pool * 2 * TWIN_HIT_SHARE * (broken ? TWINS_BREAK_DAMAGE_MUL : 1))
      if (t.lost >= t.pool - 1e-6 && !t.down) { t.down = true; t.move = null; t.booked = [] }
    }
    boss = { ...boss, hp: Math.max(floor, floor + remainOf(f)) }
  }
  // 体幹が満ちて崩れた・まだ hp が残る → 致命ではなく「崩れ」(2.6秒・削りが3倍)。
  if (boss.state === 'staggered' && boss.hp > floor) {
    f.brokenUntil = now + TWINS_BREAK_MS
    for (const t of f.twins) { t.move = null; t.booked = [] }
    if (f.dev) { f.dev = null; f.devNext = now + TWINS_BREAK_MS + 600 }
    boss = { ...boss, state: 'idle', stateElapsed: 0, posture: 0 }
  }
  // 二人とも倒れた (装置が壊れた) → 普段の崩れ → 致命へ。
  if (boss.hp <= floor && boss.state !== 'staggered') boss = staggerBoss({ ...boss, hp: floor })
  return boss.state === 'staggered' ? boss : placeBoss(f, boss, player, now, w)
}

/** 斬れる部品へ当てた・行軍の駒を弾いた分だけ装置が減る (ラボの devHp60 の比率)。 */
function devWear(f: TwinsFight, now: number, struck: boolean, hits: readonly ActiveHit[], sfx?: SfxId[]): void {
  const unit = f.devPool / 60
  if (struck && f.target && f.dev) {
    if (f.target.kind === 'wall') {
      f.dev.wallHp--; f.devLost += 4 * unit
      if (planA && !returnMode()) cutPart(f, 'wall', now, sfx)
      if (f.dev.wallHp <= 0) { f.dev.wallUntil = now; f.devLost += 6 * unit }
    } else {
      f.devLost += 5 * unit
      if (!returnMode() && (f.target.kind === 'curtain' || f.target.kind === 'light')) cutPart(f, f.target.kind, now, sfx)
    }
  }
  for (const h of hits) {
    if (f.marchIds.has(h.id) && (h.consumed || h.reflected) && !h.playerWasHit) { f.marchIds.delete(h.id); f.devLost += 3 * unit }
  }
  f.devLost = Math.min(f.devPool, f.devLost)
}

/** 案A: 部品に一太刀。残りが尽きたら壊れる (その技はもう出ない)。 */
function cutPart(f: TwinsFight, k: PartKind, now: number, sfx?: SfxId[]): void {
  const p = f.parts[k]
  if (p.brokenAt) return
  p.hp = Math.max(0, p.hp - 1)
  if (p.hp > 0) return
  p.brokenAt = now
  // 出ている技はその場で終わる (壊すのはどれも床を叩いた後なので、当たりは残っていない)
  if (f.dev?.kind === k) f.dev.end = now
  sfx?.push('hit_boss_heavy')
}

/** 案A: 壊した部品から決まる、体幹の貯まり方の倍率 (壊した部品の倍率の掛け算・1 = 何も壊していない)。 */
export function twinsPartMul(f: TwinsFight): number {
  let m = 1
  for (const k of Object.keys(PLAN_A_PARTS) as PartKind[]) if (f.parts[k].brokenAt) m *= PLAN_A_PARTS[k].mul
  return m
}

/** 案A の3部屋目 (11-04 作者の答え room3=posture)。装置も体幹で取る: 斬ると装置は減るが、体力は床の1つ上で止まり、
 *  体幹が満ちた時だけそのまま致命 (崩れの2.6秒は挟まない)。体幹の貯まりは壊した部品で変わる (part=c・twinsPartMul)。 */
function planADeviceAfterHits(f: TwinsFight, prev: Boss, boss: Boss, player: Player, now: number, hits: readonly ActiveHit[], floor: number, staggerBoss: (b: Boss) => Boss, sfx?: SfxId[]): Boss {
  const ret = returnMode()
  const faltering = ret && now < f.falterUntil
  // 倍率はこのコマの太刀より前に壊れていた部品で決める (壊した一太刀そのものには掛けない)。打ち返す版はよろめきの間だけ倍。
  const mul = ret ? (faltering ? RETURN.falterMul : 1) : twinsPartMul(f)
  devWear(f, now, boss.hp < prev.hp, hits, sfx)
  // 案A の勝ちは体幹だけ。削りで装置が尽きると技も的も止まり、体幹を貯める手が無くなるので、尽きる手前で止める。
  f.devLost = Math.min(f.devLost, f.devPool - 1)
  const gain = boss.posture - prev.posture
  let posture = gain > 0 ? Math.min(boss.maxPosture, prev.posture + gain * mul) : boss.posture
  // よろめきの間は体幹が戻らない。
  if (faltering) posture = Math.max(posture, prev.posture)
  if (ret) posture = Math.min(boss.maxPosture, posture + returnAfterHits(f, now, hits, player, walls(boss), sfx) * boss.maxPosture * RETURN.posture)
  boss = { ...boss, hp: floor + Math.max(1, f.devPool - f.devLost), posture }
  if (boss.state !== 'staggered' && boss.posture >= boss.maxPosture) {
    boss = staggerBoss(boss)
    sfx?.push('stagger')
  }
  if (boss.state === 'staggered') {
    if (boss.posture >= boss.maxPosture) {
      f.dev = null
      return boss
    }
    boss = { ...boss, state: 'idle', stateElapsed: 0 }
    if (sfx) for (let i = sfx.length - 1; i >= 0; i--) if (sfx[i] === 'stagger') sfx.splice(i, 1)
  }
  return placeBoss(f, boss, player, now, walls(boss))
}

/** 打ち返す版: 弾いた白い駒を装置へ戻し、着いた駒の数を返す (1つにつき体幹 RETURN.posture)。 */
function returnAfterHits(f: TwinsFight, now: number, hits: readonly ActiveHit[], player: Player, w: ReturnType<typeof walls>, sfx?: SfxId[]): number {
  for (const h of hits) {
    if (!f.returnIds.has(h.id)) continue
    if (h.playerWasHit) { f.returnIds.delete(h.id); continue }
    if (h.reflected || h.consumed) {
      f.returnIds.delete(h.id); f.returned.add(h.id)
      f.flying.push({ x: h.x ?? player.x, start: now })
      // 弾かれた振り子の体は梁へ引き上げられる。
      const p = f.puppets.find((x) => x.ride?.hitId === h.id)
      if (p) { p.ride = null; p.tween = { fx: p.x, fl: p.lift, tx: hangX(p.id, w), tl: PUPPET.hangLift, start: now, ms: PUPPET.placeMs, arc: 0 } }
    }
  }
  let landed = 0
  for (let i = f.flying.length - 1; i >= 0; i--) {
    const fl = f.flying[i]!
    if (now < fl.start + RETURN.flyMs) continue
    f.flying.splice(i, 1)
    landed++
    // 吊られた二人の技は白い駒が多いので、1つの技で部品を削るのは1回まで (残りは体幹だけ)。
    const once = puppetMode() && !!f.dev && f.partDev === f.dev
    const k = once ? null : RETURN_PART_ORDER.find((x) => !f.parts[x].brokenAt) ?? null
    if (k) {
      if (puppetMode()) f.partDev = f.dev
      const p = f.parts[k]
      p.hp = Math.max(0, p.hp - 1)
      if (p.hp === 0) p.brokenAt = now
    }
    f.lastReturn = { at: now, part: k }
    sfx?.push('hit_boss_heavy')
    // よろめき: 出ている技を止め、駒を弾いた所に斬れる所を出す。吊られた二人の技は続きの合図が残る間は止めず、技の終わりまで待つ
    //   (千日手・三連打の1つ目を返した所で残りが消えないように)。
    if (puppetMode() && f.dev && (f.dev.cues ?? []).some((x) => !x.done)) { f.falterQueued = fl.x; continue }
    startFalter(f, now, fl.x, w)
  }
  return landed
}

/** game.ts が twinsAfterHits の後に通す。弾いて装置へ戻した白い駒の当たりを消す (影のボスへ飛ばさない)。 */
export function twinsFilterHits(hits: ActiveHit[]): ActiveHit[] {
  const f = fight
  if (!f || f.returned.size === 0) return hits
  const out = hits.filter((h) => !f.returned.has(h.id))
  f.returned.clear()
  return out
}
/** 打ち返す版: 装置へ戻っている駒 (描画用・u は 0..1)。 */
export function twinsReturnFlights(nowSec: number): { x: number; u: number }[] {
  const f = fight
  if (!f) return []
  return f.flying.map((fl) => ({ x: fl.x, u: Math.max(0, Math.min(1, (nowSec * 1000 - fl.start) / RETURN.flyMs)) }))
}
/** 打ち返す版: よろめきの間か (描画用)。 */
export function twinsFaltering(nowSec: number): boolean { return returnMode() && !!fight && nowSec * 1000 < fight.falterUntil }

/** 案A の1・2部屋目。体力は二人の持ち分を減らすだけで致命には繋げず、致命は体幹が満ちた時だけ。 */
function planAAfterHits(f: TwinsFight, prev: Boss, boss: Boss, player: Player, now: number, hits: readonly ActiveHit[], floor: number, staggerBoss: (b: Boss) => Boss, sfx?: SfxId[]): Boss {
  const w = walls(boss)
  // 弾き返した鈴: 前のコマに飛んでいて、壁の内側で消えた物はボスに当たった (hits.ts が体力 6・体幹を足して消す)。体力の分は戻す。
  const alive = new Map<number, number>()
  for (const h of hits) {
    if (h.follow === 'travel' && h.reflected && !h.consumed && h.attackId.startsWith('twins_') && h.attackId !== 'twins_dev_march' && h.x !== undefined) alive.set(h.id, h.x)
  }
  let bellHits = 0
  for (const [id, x] of f.bells) if (!alive.has(id) && x > w.left - 20 && x < w.right + 20) bellHits++
  f.bells = alive
  const drop = Math.max(0, prev.hp - boss.hp)
  const swordDrop = Math.max(0, drop - bellHits * REFLECTED_BELL_HP)
  const collapsed = now < f.collapseUntil
  let posture = boss.posture
  if (collapsed) {
    // 総崩れの間は体幹が戻らない。斬るたびに大きく貯まる。
    posture = Math.max(posture, prev.posture)
    if (swordDrop > 0) posture += boss.maxPosture * PLAN_A.collapsePosture
  }
  if (swordDrop > 0 && f.target?.kind === 'twin') {
    const id = f.target.id
    const t = f.twins.find((x) => x.id === id)!
    if (!t.down && !twinRising(t, now)) {
      t.lost = Math.min(t.pool, t.lost + t.pool * 2 * TWIN_HIT_SHARE)
      if (t.lost >= t.pool - 1e-6) {
        t.down = true; t.move = null; t.booked = []; t.downUntil = now + PLAN_A.downMs
        if (f.twins.every((x) => x.down)) {
          f.collapseUntil = now + PLAN_A.collapseMs
          for (const x of f.twins) x.downUntil = f.collapseUntil
        }
      }
    }
  }
  posture = Math.min(boss.maxPosture, posture)
  // 体力は床の1つ上で止める (床に届くと致命の扉が開くため)。
  boss = { ...boss, hp: floor + Math.max(1, remainOf(f)), posture }
  // 総崩れの上乗せで満ちた時は、ここで崩す (当たりの処理は上乗せの前に崩れを見ている)。
  if (boss.state !== 'staggered' && boss.posture >= boss.maxPosture) {
    boss = staggerBoss(boss)
    sfx?.push('stagger')
  }
  if (boss.state === 'staggered') {
    if (boss.posture >= boss.maxPosture) {
      // 体幹で崩れた → そのまま致命へ (崩れの2.6秒に変えない)。二人の技は止める。
      f.collapseUntil = 0
      for (const t of f.twins) { t.move = null; t.booked = [] }
      return boss
    }
    // 体力が床に届いて開いた崩れは取り消す (致命は体幹だけ)。
    boss = { ...boss, state: 'idle', stateElapsed: 0 }
    if (sfx) for (let i = sfx.length - 1; i >= 0; i--) if (sfx[i] === 'stagger') sfx.splice(i, 1)
  }
  return placeBoss(f, boss, player, now, w)
}

/** 3部屋目の装置がもう見えているか (現れの途中は見えない)。 */
export function twinsDeviceShown(f: TwinsFight, now: number): boolean {
  return f.room === 3 && (!f.reveal || now >= f.reveal.start + revealAppearMs())
}

/** 幕の印 (HUD) の数と点き方。1・2部屋目と3部屋目の現れの前は2つに見せ、3幕目は現れた時に3つ目として右に加わる
 *  (10-30 作者「最初は2幕にしか見えないようにしてほしい」)。lamp_snuff の印の番号もここで置き換える。 */
export function twinsActMarks(boss: Boss, nowSec: number): { count: number; lit: boolean[]; snuffIndex: (i: number) => number } | null {
  if (!twinsFightOn(boss)) return null
  const f = fight
  const shown = !!f && f.roomKey === boss.executesTaken && twinsDeviceShown(f, nowSec * 1000)
  if (!shown) {
    const lit = Math.max(0, 2 - boss.executesTaken)
    return { count: 2, lit: [0, 1].map((i) => i < lit), snuffIndex: (i) => i - 1 }
  }
  return { count: 3, lit: [false, false, boss.executesTaken < 3], snuffIndex: () => 2 }
}

/** 崩れの間か (描画用)。 */
export function twinsBroken(nowSec: number): boolean { return !!fight && nowSec * 1000 < fight.brokenUntil }
